import { FC, MutableRefObject, useEffect, useRef, useState } from 'react';

import { ActionInputContextType, VideoRecordingEvent } from '@bonusly/common-action-input';
import { useBreakpointValue, useDisclosure } from '@bonusly/design-system';
import { useDispatch, useEventListener } from '@bonusly/events';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { VideoDevices } from '../../../types/video';
import { BonusVideoRecordingModalDesktop } from './bonus-video-recording-modal-desktop';
import { BonusVideoRecordingModalMobile } from './bonus-video-recording-modal-mobile';
import { useRecordingTimer } from './hooks/use-recording-timer';
import { useVideoKeyboardControls } from './hooks/use-video-keyboard-controls';
import { useVideoModal } from './hooks/use-video-modal';
import { useVideoRecorder } from './hooks/use-video-recording';

export interface VideoModalProps {
	isOpen: boolean;
	onClose: () => void;
	videoRef: MutableRefObject<HTMLVideoElement | null>;
	recordedVideoRef: MutableRefObject<HTMLVideoElement | null>;
	isRecording: boolean;
	recordedVideo: string | null;
	recordedPreviewReady: boolean;
	videoPoster?: string;
	onStartRecording: () => void;
	onStopRecording: () => void;
	onRestartRecording: () => void;
	onDownload: () => void;
	onSubmit: () => void;
	obtainedDevicePermissionRef: MutableRefObject<boolean>;
	selectedAudioDeviceRef: MutableRefObject<string | undefined>;
	selectedVideoDeviceRef: MutableRefObject<string | undefined>;
	selectedVideoDeviceId: string;
	selectedAudioDeviceId: string;
	devices: VideoDevices;
	handleDeviceChange: (deviceId: string, kind: 'video' | 'audio') => void;
	countdown: number | null | '';
	formattedTime: string;
	totalTime: string;
	timerColor: string;
	confirmModalIsOpen: boolean;
	confirmModalOnClose: () => void;
	onConfirmModalClose: () => void;
	getVideoFilename: () => string;
	contextType: ActionInputContextType;
	isLoadingSuggestedReason: boolean;
	aiProcessingEnabled: boolean;
	onToggleAiProcessing: () => void;
	getSpeechTags: () => Promise<any>;
}

export const BonusVideoRecordingModal: FC = () => {
	const { isOpen, onClose: baseOnClose, onOpen } = useDisclosure();
	const { isOpen: confirmModalIsOpen, onClose: confirmModalOnClose, onOpen: confirmModalOnOpen } = useDisclosure();
	const [contextType, setContextType] = useState<ActionInputContextType>(ActionInputContextType.Recognition);
	const [aiProcessingEnabled, setAiProcessingEnabled] = useState(false);
	const dispatch = useDispatch();
	const mobileVideoFeatureFlag = useFeatureFlag('mobile-video');

	useEffect(() => {
		if (mobileVideoFeatureFlag) {
			setAiProcessingEnabled(true);
		}
	}, []);

	const videoRef = useRef<HTMLVideoElement | null>(null);
	const recordedVideoRef = useRef<HTMLVideoElement | null>(null);
	const openedByVideoCarouselRef = useRef<boolean>(false);

	const isMobile = useBreakpointValue({ base: true, sm: false });

	const {
		isRecording,
		recordedVideo,
		startRecording,
		stopRecording,
		restartRecording,
		downloadVideo,
		handleDeviceChange,
		obtainedDevicePermissionRef,
		selectedAudioDeviceRef,
		resetRecordingState,
		selectedVideoDeviceRef,
		devices,
		recordingAttempts,
		countdown,
		recordedPreviewReady,
		videoStream,
		getVideoFilename,
		videoPoster,
		getSuggestedBonusReason,
		isLoadingSuggestedReason,
		getSpeechTags,
	} = useVideoRecorder(videoRef, recordedVideoRef, isOpen, isMobile);

	const { formattedTime, totalTime, timerColor } = useRecordingTimer(isRecording, stopRecording);

	useEventListener(VideoRecordingEvent.OpenModal, (payload) => {
		setContextType(payload?.contextType || ActionInputContextType.Recognition);
		openedByVideoCarouselRef.current = payload?.source === 'video-carousel';
		onOpen();
	});

	useEffect(() => {
		if (videoStream && videoRef.current) {
			videoRef.current.srcObject = videoStream;
		}
	}, [videoStream]);

	useEffect(() => {
		if (recordedVideoRef.current && recordedVideo) {
			recordedVideoRef.current.load();
		}
	}, [recordedVideo]);

	useVideoKeyboardControls({
		isOpen,
		showRecordButton: !recordedVideo && !isRecording && countdown === null,
		isRecording,
		startRecording,
		restartRecording,
		stopRecording,
	});

	const handleClose = () => {
		// If the modal was opened by the video carousel,
		// and no video was recorded, we'll dispatch an event.
		// The use-give-modal hook listens to that event and will
		// close the give modal, which is opened underneath this one.
		// The reason is we know the user's intent is to either give
		// video recognition, or not, when exploring the carousel. No video,
		// means no intent to give.
		if (openedByVideoCarouselRef.current && !recordedVideo) {
			dispatch(VideoRecordingEvent.ClosedCarouselModalWithNoVideo);
		}
		baseOnClose();
	};

	const { handleSubmit, handleVideoRecordingModalClose, handleVideoModalConfirmBeforeClose } = useVideoModal({
		isOpen,
		onClose: handleClose,
		onOpen,
		recordedVideo,
		isRecording,
		recordingAttempts,
		resetRecordingState,
		confirmModalOnClose,
		confirmModalOnOpen,
		getSuggestedBonusReason,
		aiProcessingEnabled,
	});

	const modalProps: VideoModalProps = {
		isOpen,
		onClose: handleVideoModalConfirmBeforeClose,
		videoRef,
		recordedVideoRef,
		isRecording,
		recordedVideo,
		recordedPreviewReady,
		videoPoster: videoPoster || undefined,
		onStartRecording: startRecording,
		onStopRecording: stopRecording,
		onRestartRecording: restartRecording,
		onDownload: downloadVideo,
		onSubmit: handleSubmit,
		obtainedDevicePermissionRef,
		selectedAudioDeviceRef,
		selectedVideoDeviceRef,
		selectedVideoDeviceId: selectedVideoDeviceRef.current || '',
		selectedAudioDeviceId: selectedAudioDeviceRef.current || '',
		devices: devices as VideoDevices,
		handleDeviceChange,
		countdown,
		formattedTime,
		totalTime,
		timerColor,
		confirmModalIsOpen,
		confirmModalOnClose,
		onConfirmModalClose: handleVideoRecordingModalClose,
		getVideoFilename,
		contextType,
		isLoadingSuggestedReason,
		aiProcessingEnabled,
		onToggleAiProcessing: () => setAiProcessingEnabled(!aiProcessingEnabled),
		getSpeechTags,
	};

	const ModalComponent = isMobile ? BonusVideoRecordingModalMobile : BonusVideoRecordingModalDesktop;
	return <ModalComponent {...modalProps} />;
};
